import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, Users, Search, Edit, Trash2, Phone, 
  Calendar, DollarSign, CalendarDays, AlertTriangle,
  Gift, Clock, XCircle, Wallet, Award, Briefcase, Timer, Printer, RefreshCw
} from 'lucide-react';
import { formatCPF, formatPhone, formatDateDisplay, formatCurrency } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('lista');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const [overtimeDialogOpen, setOvertimeDialogOpen] = useState(false);
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
  const [prolaboreDialogOpen, setProlaboreDialogOpen] = useState(false);
  
  // Selected employee for operations
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Vacation queue
  const [vacationQueue, setVacationQueue] = useState([]);
  const [activeVacations, setActiveVacations] = useState([]);
  const [syncingVacations, setSyncingVacations] = useState(false);
  
  // Payrolls
  const [payrolls, setPayrolls] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  
  // Pró-labore
  const [prolabores, setProlabores] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    nome: '', cpf: '', rg: '', pis: '', ctps: '',
    telefone: '', endereco: '', cargo: '', data_admissao: '',
    salario_base: '', banco: '', agencia: '', conta: '', pix: '',
    ultima_ferias: ''
  });
  
  const [absenceData, setAbsenceData] = useState({ data: '', motivo: '', justificada: false, tipo: 'dia_inteiro', valor_personalizado: '' });
  const [advanceData, setAdvanceData] = useState({ tipo: 'salario', valor: '', data: '', conta_id: '', observacao: '' });
  const [bonusData, setBonusData] = useState({ valor: '', data: '', descricao: '' });
  const [overtimeData, setOvertimeData] = useState({ data: '', horas: '', valor_hora: '', tipo: '50', observacao: '' });
  const [payrollData, setPayrollData] = useState({ ano: new Date().getFullYear(), mes: new Date().getMonth() + 1, quinzena: 1, conta_id: '' });
  const [vacationData, setVacationData] = useState({ data_inicio: '', data_fim: '', dias: 30, valor_ferias: '', valor_abono: '', desconto_previdencia: '', observacao: '' });
  const [prolaboreData, setProlaboreData] = useState({ beneficiario: '', valor: '', data: '', conta_id: '', observacao: '' });
  const [adiantamentosFeriasPendentes, setAdiantamentosFeriasPendentes] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, accountsRes, queueRes, payrollRes, prolaboreRes, vacationsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/bank-accounts`),
        axios.get(`${API}/vacations/queue`),
        axios.get(`${API}/payroll`),
        axios.get(`${API}/prolabores`),
        axios.get(`${API}/vacations`)
      ]);
      setEmployees(empRes.data);
      setBankAccounts(accountsRes.data);
      setVacationQueue(queueRes.data);
      setPayrolls(payrollRes.data);
      setProlabores(prolaboreRes.data);
      // Filtrar férias ativas (agendadas ou em andamento)
      setActiveVacations(vacationsRes.data.filter(v => v.status === 'agendada' || v.status === 'em_andamento'));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncVacations = async () => {
    setSyncingVacations(true);
    try {
      const res = await axios.post(`${API}/vacations/sync-status`);
      if (res.data.ferias_iniciadas > 0 || res.data.ferias_concluidas > 0) {
        toast.success(`Sincronizado: ${res.data.ferias_iniciadas} iniciadas, ${res.data.ferias_concluidas} concluídas`);
        fetchData();
      } else {
        toast.info('Nenhuma atualização necessária');
      }
    } catch (error) {
      toast.error('Erro ao sincronizar férias');
    } finally {
      setSyncingVacations(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '', cpf: '', rg: '', pis: '', ctps: '',
      telefone: '', endereco: '', cargo: '', data_admissao: '',
      salario_base: '', banco: '', agencia: '', conta: '', pix: '',
      ultima_ferias: ''
    });
    setEditingEmployee(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.cpf || !formData.cargo || !formData.data_admissao || !formData.salario_base) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const payload = {
        ...formData,
        salario_base: parseFloat(formData.salario_base),
        ultima_ferias: formData.ultima_ferias || null
      };

      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, payload);
        toast.success('Funcionário atualizado!');
      } else {
        await axios.post(`${API}/employees`, payload);
        toast.success('Funcionário cadastrado!');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar funcionário');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      nome: employee.nome,
      cpf: employee.cpf,
      rg: employee.rg || '',
      pis: employee.pis || '',
      ctps: employee.ctps || '',
      telefone: employee.telefone || '',
      endereco: employee.endereco || '',
      cargo: employee.cargo,
      data_admissao: employee.data_admissao,
      salario_base: employee.salario_base.toString(),
      banco: employee.banco || '',
      agencia: employee.agencia || '',
      conta: employee.conta || '',
      pix: employee.pix || '',
      ultima_ferias: employee.ultima_ferias || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este funcionário?')) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success('Funcionário excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir funcionário');
    }
  };

  // Absence handlers
  const handleAddAbsence = async () => {
    if (!absenceData.data) {
      toast.error('Selecione a data da falta');
      return;
    }
    try {
      const payload = {
        employee_id: selectedEmployee.id,
        data: absenceData.data,
        motivo: absenceData.motivo,
        justificada: absenceData.justificada,
        tipo: absenceData.tipo,
        valor_personalizado: absenceData.valor_personalizado ? parseFloat(absenceData.valor_personalizado) : null
      };
      await axios.post(`${API}/absences`, payload);
      toast.success('Falta registrada!');
      setAbsenceDialogOpen(false);
      setAbsenceData({ data: '', motivo: '', justificada: false, tipo: 'dia_inteiro', valor_personalizado: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar falta');
    }
  };

  // Advance handlers
  const handleAddAdvance = async () => {
    if (!advanceData.valor || !advanceData.data) {
      toast.error('Preencha valor e data');
      return;
    }
    try {
      await axios.post(`${API}/advances`, {
        employee_id: selectedEmployee.id,
        ...advanceData,
        valor: parseFloat(advanceData.valor),
        conta_id: advanceData.conta_id || null
      });
      toast.success('Adiantamento registrado!');
      setAdvanceDialogOpen(false);
      setAdvanceData({ tipo: 'salario', valor: '', data: '', conta_id: '', observacao: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar adiantamento');
    }
  };

  // Bonus handlers
  const handleAddBonus = async () => {
    if (!bonusData.valor || !bonusData.data || !bonusData.descricao) {
      toast.error('Preencha todos os campos');
      return;
    }
    try {
      await axios.post(`${API}/bonuses`, {
        employee_id: selectedEmployee.id,
        ...bonusData,
        valor: parseFloat(bonusData.valor)
      });
      toast.success('Bonificação registrada!');
      setBonusDialogOpen(false);
      setBonusData({ valor: '', data: '', descricao: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar bonificação');
    }
  };

  // Overtime handlers
  const handleAddOvertime = async () => {
    if (!overtimeData.data || !overtimeData.horas || !overtimeData.valor_hora) {
      toast.error('Preencha data, horas e valor da hora');
      return;
    }
    try {
      await axios.post(`${API}/overtimes`, {
        employee_id: selectedEmployee.id,
        data: overtimeData.data,
        horas: parseFloat(overtimeData.horas),
        valor_hora: parseFloat(overtimeData.valor_hora),
        tipo: overtimeData.tipo,
        observacao: overtimeData.observacao
      });
      toast.success('Hora extra registrada!');
      setOvertimeDialogOpen(false);
      setOvertimeData({ data: '', horas: '', valor_hora: '', tipo: '50', observacao: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar hora extra');
    }
  };

  // Payroll batch handlers
  const handleCreateBatch = async () => {
    setBatchLoading(true);
    try {
      const res = await axios.post(`${API}/payroll/batch`, {
        ano: payrollData.ano,
        mes: payrollData.mes,
        quinzena: payrollData.quinzena,
        conta_id: payrollData.conta_id || null
      });
      toast.success(`Folha gerada para ${res.data.criados.length} funcionários! Total: ${formatCurrency(res.data.total_folha)}`);
      if (res.data.pulados.length > 0) {
        toast.info(`${res.data.pulados.length} funcionário(s) já tinham folha para este período`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar folha em lote');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleCloseBatch = async () => {
    if (!window.confirm(`Fechar todas as folhas de ${MESES[payrollData.mes - 1]}/${payrollData.ano} - Q${payrollData.quinzena}? Isso gerará contas a pagar.`)) return;
    
    setBatchLoading(true);
    try {
      const res = await axios.post(`${API}/payroll/batch/close?ano=${payrollData.ano}&mes=${payrollData.mes}&quinzena=${payrollData.quinzena}`);
      toast.success(`${res.data.fechados.length} folhas fechadas! Total: ${formatCurrency(res.data.total)}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fechar folhas');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleReopenBatch = async () => {
    if (!window.confirm(`Reabrir todas as folhas FECHADAS de ${MESES[payrollData.mes - 1]}/${payrollData.ano} - Q${payrollData.quinzena}? Isso permitirá gerar novamente.`)) return;
    
    setBatchLoading(true);
    try {
      const res = await axios.post(`${API}/payroll/batch/reopen?ano=${payrollData.ano}&mes=${payrollData.mes}&quinzena=${payrollData.quinzena}`);
      toast.success(`${res.data.reabertos.length} folhas reabertas!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reabrir folhas');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleRecalculateBatch = async () => {
    if (!window.confirm(`Recalcular todas as folhas ABERTAS de ${MESES[payrollData.mes - 1]}/${payrollData.ano} - Q${payrollData.quinzena}? Isso atualizará bônus, faltas e horas extras.`)) return;
    
    setBatchLoading(true);
    try {
      const res = await axios.post(`${API}/payroll/batch/recalculate?ano=${payrollData.ano}&mes=${payrollData.mes}&quinzena=${payrollData.quinzena}`);
      toast.success(`${res.data.recalculados.length} folhas recalculadas!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao recalcular folhas');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleDeletePayroll = async (id) => {
    if (!window.confirm('Excluir esta folha?')) return;
    try {
      await axios.delete(`${API}/payroll/${id}`);
      toast.success('Folha excluída!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleClosePayroll = async (id) => {
    try {
      await axios.post(`${API}/payroll/${id}/close`);
      toast.success('Folha fechada! Conta a pagar gerada.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fechar folha');
    }
  };

  // Print payroll function
  const handlePrintPayroll = () => {
    // Buscar dados bancários dos funcionários
    const payrollsWithBankInfo = filteredPayrolls.map(p => {
      const emp = employees.find(e => e.id === p.employee_id);
      return {
        ...p,
        banco: emp?.banco || '-',
        agencia: emp?.agencia || '-',
        conta: emp?.conta || '-',
        pix: emp?.pix || '-'
      };
    });

    const totalLiquido = payrollsWithBankInfo.reduce((sum, p) => sum + (p.valor_liquido || 0), 0);

    const formatPrintCurrency = (val) => {
      return Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Folha de Pagamento - ${MESES[payrollData.mes - 1]}/${payrollData.ano} - Q${payrollData.quinzena}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .header h1 { font-size: 18px; margin-bottom: 5px; }
          .header p { color: #666; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .total-row { background-color: #e8f5e9; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #666; }
          .bank-info { font-size: 10px; color: #666; }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>FOLHA DE PAGAMENTO</h1>
          <p>${MESES[payrollData.mes - 1]}/${payrollData.ano} - ${payrollData.quinzena === 1 ? '1ª Quinzena (dia 15)' : '2ª Quinzena (dia 30)'}</p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Funcionário</th>
              <th>Dados Bancários</th>
              <th class="text-right">Salário</th>
              <th class="text-right">H.E.</th>
              <th class="text-right">Bônus</th>
              <th class="text-right">Descontos</th>
              <th class="text-right">Valor a Pagar</th>
            </tr>
          </thead>
          <tbody>
            ${payrollsWithBankInfo.map(p => `
              <tr>
                <td>
                  <strong>${p.employee_nome}</strong>
                </td>
                <td class="bank-info">
                  ${p.pix && p.pix !== '-' ? `<strong>PIX:</strong> ${p.pix}<br>` : ''}
                  ${p.banco !== '-' ? `${p.banco} | Ag: ${p.agencia} | Cc: ${p.conta}` : 'Não informado'}
                </td>
                <td class="text-right">R$ ${formatPrintCurrency(p.salario_base_quinzena)}</td>
                <td class="text-right" style="color: green;">+R$ ${formatPrintCurrency(p.valor_horas_extras)}</td>
                <td class="text-right" style="color: blue;">+R$ ${formatPrintCurrency(p.bonus || 0)}</td>
                <td class="text-right" style="color: red;">-R$ ${formatPrintCurrency((p.desconto_faltas || 0) + (p.adiantamentos_descontados || 0))}</td>
                <td class="text-right"><strong>R$ ${formatPrintCurrency(p.valor_liquido)}</strong></td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="6" class="text-right"><strong>TOTAL A PAGAR:</strong></td>
              <td class="text-right"><strong>R$ ${formatPrintCurrency(totalLiquido)}</strong></td>
            </tr>
          </tbody>
        </table>
        
        <div style="margin-top: 40px;">
          <p><strong>Observações:</strong></p>
          <ul style="margin-left: 20px; margin-top: 10px;">
            <li>H.E. = Horas Extras</li>
            <li>Bônus = Comissões e Bonificações</li>
            <li>Descontos incluem: Faltas e Adiantamentos</li>
          </ul>
        </div>
        
        <div class="footer">
          <p>Documento gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Vacation handlers
  const handleAddVacation = async () => {
    if (!vacationData.data_inicio || !vacationData.data_fim || !vacationData.valor_ferias) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      await axios.post(`${API}/vacations`, {
        employee_id: selectedEmployee.id,
        ...vacationData,
        dias: parseInt(vacationData.dias),
        valor_ferias: parseFloat(vacationData.valor_ferias),
        valor_abono: parseFloat(vacationData.valor_abono) || 0,
        desconto_previdencia: parseFloat(vacationData.desconto_previdencia) || 0
      });
      toast.success('Férias registradas!');
      setVacationDialogOpen(false);
      setVacationData({ data_inicio: '', data_fim: '', dias: 30, valor_ferias: '', valor_abono: '', desconto_previdencia: '', observacao: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar férias');
    }
  };

  // Pró-labore handlers
  const handleAddProlabore = async () => {
    if (!prolaboreData.beneficiario || !prolaboreData.valor || !prolaboreData.data) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      await axios.post(`${API}/prolabores`, {
        beneficiario: prolaboreData.beneficiario,
        valor: parseFloat(prolaboreData.valor),
        data: prolaboreData.data,
        conta_id: prolaboreData.conta_id || null,
        observacao: prolaboreData.observacao || null
      });
      toast.success('Pró-labore registrado!');
      setProlaboreDialogOpen(false);
      setProlaboreData({ beneficiario: '', valor: '', data: '', conta_id: '', observacao: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar pró-labore');
    }
  };

  const handleDeleteProlabore = async (id) => {
    if (!window.confirm('Excluir este pró-labore?')) return;
    try {
      await axios.delete(`${API}/prolabores/${id}`);
      toast.success('Pró-labore excluído!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // Bônus handlers
  const handleDeleteBonus = async (id) => {
    if (!window.confirm('Excluir este bônus/comissão?')) return;
    try {
      await axios.delete(`${API}/bonuses/${id}`);
      toast.success('Bônus excluído!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // Obter todos os bônus de todos os funcionários
  const allBonuses = employees.flatMap(emp => 
    (emp.bonuses || []).map(b => ({ ...b, employee_nome: emp.nome }))
  ).sort((a, b) => b.data.localeCompare(a.data));

  const filteredEmployees = employees.filter(emp =>
    emp.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.cpf.includes(searchTerm) ||
    emp.cargo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalFolha = employees.reduce((sum, emp) => sum + (emp.ativo ? emp.salario_base : 0), 0);
  const totalProlabore = prolabores.reduce((sum, p) => sum + (p.valor || 0), 0);

  // Filtrar folhas pelo período selecionado
  const filteredPayrolls = payrolls.filter(p => 
    p.ano === payrollData.ano && p.mes === payrollData.mes && p.quinzena === payrollData.quinzena
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando funcionários...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Funcionários</h1>
            <p className="text-muted-foreground text-sm">Gerencie sua equipe e folha de pagamento</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-employee-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo Funcionário
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Funcionários</p>
                  <p className="text-xl font-bold">{employees.filter(e => e.ativo).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <DollarSign className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Folha Mensal</p>
                  <p className="text-lg font-bold">{formatCurrency(totalFolha)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Briefcase className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pró-labore Pend.</p>
                  <p className="text-lg font-bold">{formatCurrency(totalProlabore)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <CalendarDays className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Férias Pendentes</p>
                  <p className="text-xl font-bold">{vacationQueue.filter(v => v.tem_direito).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Clock className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Folhas Abertas</p>
                  <p className="text-xl font-bold">{filteredPayrolls.filter(p => p.status === 'rascunho').length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto">
            <TabsTrigger value="lista" className="text-xs sm:text-sm py-2">Funcionários</TabsTrigger>
            <TabsTrigger value="folha" className="text-xs sm:text-sm py-2">Folha</TabsTrigger>
            <TabsTrigger value="ferias" className="text-xs sm:text-sm py-2">Férias</TabsTrigger>
            <TabsTrigger value="adiantamentos" className="text-xs sm:text-sm py-2">Adiant.</TabsTrigger>
            <TabsTrigger value="bonus" className="text-xs sm:text-sm py-2">Bônus</TabsTrigger>
            <TabsTrigger value="prolabore" className="text-xs sm:text-sm py-2">Pró-labore</TabsTrigger>
          </TabsList>

          {/* Tab: Lista de Funcionários */}
          <TabsContent value="lista" className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, CPF ou cargo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-3">
              {filteredEmployees.map((emp) => (
                <Card key={emp.id} className={!emp.ativo ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{emp.nome}</h3>
                          <Badge variant={emp.ativo ? 'default' : 'secondary'}>
                            {emp.ativo ? 'Ativo' : 'Inativo'}
                          </Badge>
                          {emp.status === 'ferias_agendadas' && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                              Férias Agendadas
                            </Badge>
                          )}
                          {emp.status === 'em_ferias' && (
                            <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
                              Em Férias
                            </Badge>
                          )}
                          {emp.status === 'afastado' && (
                            <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                              Afastado
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{emp.cargo}</p>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                          <span>CPF: {emp.cpf}</span>
                          {emp.telefone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {emp.telefone}</span>}
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Admissão: {formatDateDisplay(emp.data_admissao)}</span>
                          {emp.ultima_ferias && (
                            <span className="flex items-center gap-1 text-blue-500">
                              <Calendar className="h-3 w-3" /> Últimas Férias: {formatDateDisplay(emp.ultima_ferias)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:items-end gap-2">
                        <p className="font-bold text-lg text-green-500">
                          {formatCurrency(emp.salario_base)}
                        </p>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => { setSelectedEmployee(emp); setAbsenceDialogOpen(true); }} title="Registrar Falta">
                            <XCircle className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedEmployee(emp); setAdvanceDialogOpen(true); }} title="Adiantamento">
                            <Wallet className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedEmployee(emp); setBonusDialogOpen(true); }} title="Bônus">
                            <Award className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedEmployee(emp); setOvertimeDialogOpen(true); }} title="Hora Extra">
                            <Timer className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(emp)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(emp.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredEmployees.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    Nenhum funcionário encontrado
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Tab: Folha de Pagamento */}
          <TabsContent value="folha" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5" /> Gerar Folha de Pagamento em Lote
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Mês</Label>
                    <Select value={payrollData.mes.toString()} onValueChange={(v) => setPayrollData({ ...payrollData, mes: parseInt(v) })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MESES.map((m, i) => (
                          <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Ano</Label>
                    <Input type="number" value={payrollData.ano} onChange={(e) => setPayrollData({ ...payrollData, ano: parseInt(e.target.value) })} />
                  </div>
                  <div>
                    <Label className="text-xs">Quinzena</Label>
                    <Select value={payrollData.quinzena.toString()} onValueChange={(v) => setPayrollData({ ...payrollData, quinzena: parseInt(v) })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1ª (dia 15)</SelectItem>
                        <SelectItem value="2">2ª (dia 30)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Conta (opcional)</Label>
                    <Select value={payrollData.conta_id || 'none'} onValueChange={(v) => setPayrollData({ ...payrollData, conta_id: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhuma</SelectItem>
                        {bankAccounts.map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>{acc.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={handleCreateBatch} disabled={batchLoading}>
                    {batchLoading ? 'Gerando...' : 'Gerar Folha em Lote'}
                  </Button>
                  {filteredPayrolls.filter(p => p.status === 'rascunho').length > 0 && (
                    <Button variant="secondary" onClick={handleRecalculateBatch} disabled={batchLoading}>
                      Recalcular ({filteredPayrolls.filter(p => p.status === 'rascunho').length})
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleCloseBatch} disabled={batchLoading || filteredPayrolls.filter(p => p.status === 'rascunho').length === 0}>
                    Fechar Todas ({filteredPayrolls.filter(p => p.status === 'rascunho').length})
                  </Button>
                  {filteredPayrolls.filter(p => p.status === 'fechado').length > 0 && (
                    <Button variant="destructive" size="sm" onClick={handleReopenBatch} disabled={batchLoading}>
                      Reabrir Fechadas ({filteredPayrolls.filter(p => p.status === 'fechado').length})
                    </Button>
                  )}
                  <Button variant="outline" onClick={handlePrintPayroll} disabled={filteredPayrolls.length === 0}>
                    <Printer className="h-4 w-4 mr-2" /> Imprimir
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Lista de Folhas do Período */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Folhas - {MESES[payrollData.mes - 1]}/{payrollData.ano} - Q{payrollData.quinzena}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredPayrolls.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Nenhuma folha gerada para este período</p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-medium p-2 bg-muted rounded">
                      <span>Total do período:</span>
                      <span className="text-green-500">{formatCurrency(filteredPayrolls.reduce((sum, p) => sum + p.valor_liquido, 0))}</span>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Funcionário</TableHead>
                          <TableHead className="text-right">Salário</TableHead>
                          <TableHead className="text-right">H.E.</TableHead>
                          <TableHead className="text-right">Bônus</TableHead>
                          <TableHead className="text-right">Desc.</TableHead>
                          <TableHead className="text-right">Líquido</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPayrolls.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="font-medium">{p.employee_nome}</TableCell>
                            <TableCell className="text-right">{formatCurrency(p.salario_base_quinzena || 0)}</TableCell>
                            <TableCell className="text-right text-green-500">+{formatCurrency(p.valor_horas_extras || 0)}</TableCell>
                            <TableCell className="text-right text-blue-500">+{formatCurrency(p.bonus || 0)}</TableCell>
                            <TableCell className="text-right text-destructive">-{formatCurrency((p.desconto_faltas || 0) + (p.adiantamentos_descontados || 0))}</TableCell>
                            <TableCell className="text-right font-bold">{formatCurrency(p.valor_liquido || 0)}</TableCell>
                            <TableCell>
                              <Badge variant={p.status === 'pago' ? 'default' : p.status === 'fechado' ? 'secondary' : 'outline'}>
                                {p.status === 'rascunho' ? 'Aberta' : p.status === 'fechado' ? 'Fechada' : 'Paga'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {p.status === 'rascunho' && (
                                  <>
                                    <Button size="sm" variant="outline" onClick={() => handleClosePayroll(p.id)}>
                                      Fechar
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleDeletePayroll(p.id)}>
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Férias */}
          <TabsContent value="ferias" className="space-y-4">
            {/* Férias Ativas (Agendadas ou Em Andamento) */}
            {activeVacations.length > 0 && (
              <Card className="border-blue-500/30 bg-blue-500/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Gift className="h-5 w-5 text-blue-500" /> Férias Ativas
                    </CardTitle>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleSyncVacations}
                      disabled={syncingVacations}
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${syncingVacations ? 'animate-spin' : ''}`} />
                      Sincronizar Status
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {activeVacations.map((vacation) => {
                      const emp = employees.find(e => e.id === vacation.employee_id);
                      return (
                        <div key={vacation.id} className="p-3 rounded-lg border bg-background">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                              <h4 className="font-semibold">{emp?.nome || 'Funcionário'}</h4>
                              <p className="text-xs text-muted-foreground">
                                {formatDateDisplay(vacation.data_inicio)} - {formatDateDisplay(vacation.data_fim)} ({vacation.dias} dias)
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge 
                                variant={vacation.status === 'em_andamento' ? 'default' : 'outline'}
                                className={vacation.status === 'em_andamento' ? 'bg-green-500' : 'bg-blue-50 text-blue-600 border-blue-200'}
                              >
                                {vacation.status === 'agendada' ? 'Agendada' : 'Em Andamento'}
                              </Badge>
                              <span className="text-sm font-medium text-green-500">
                                {formatCurrency(vacation.valor_liquido)}
                              </span>
                            </div>
                          </div>
                          {(vacation.adiantamentos_descontados > 0 || vacation.desconto_previdencia > 0) && (
                            <div className="mt-2 text-xs text-muted-foreground flex gap-3">
                              {vacation.adiantamentos_descontados > 0 && (
                                <span>Adiant. descontados: {formatCurrency(vacation.adiantamentos_descontados)}</span>
                              )}
                              {vacation.desconto_previdencia > 0 && (
                                <span>Previdência: {formatCurrency(vacation.desconto_previdencia)}</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarDays className="h-5 w-5" /> Fila de Férias
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {vacationQueue.map((item, idx) => (
                    <div key={item.employee_id} className={`p-4 rounded-lg border ${item.tem_direito ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-border'}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div>
                            <h4 className="font-semibold">{item.employee_nome}</h4>
                            <p className="text-xs text-muted-foreground">{item.cargo}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                          {item.tem_direito ? (
                            <Badge variant="destructive" className="flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {item.meses_vencidos} meses vencidos
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              Direito em {item.previsao_texto}
                            </Badge>
                          )}
                          {item.adiantamentos_pendentes > 0 && (
                            <Badge variant="secondary">
                              Adiant: {formatCurrency(item.adiantamentos_pendentes)}
                            </Badge>
                          )}
                          <Button
                            size="sm"
                            variant={item.tem_direito ? 'default' : 'outline'}
                            onClick={async () => {
                              setSelectedEmployee(employees.find(e => e.id === item.employee_id));
                              setVacationData({
                                ...vacationData,
                                valor_ferias: item.salario_base.toFixed(2),
                                valor_abono: (item.salario_base / 3).toFixed(2)
                              });
                              // Buscar adiantamentos de férias pendentes
                              try {
                                const advRes = await axios.get(`${API}/advances?employee_id=${item.employee_id}&tipo=ferias&descontado=false`);
                                const totalAdiant = advRes.data.reduce((sum, a) => sum + (a.valor || 0), 0);
                                setAdiantamentosFeriasPendentes(totalAdiant);
                              } catch (err) {
                                setAdiantamentosFeriasPendentes(0);
                              }
                              setVacationDialogOpen(true);
                            }}
                          >
                            <Gift className="h-3 w-3 mr-1" /> Agendar
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                        <div className="text-muted-foreground">
                          <span className="font-medium">Admissão:</span> {formatDateDisplay(item.data_admissao)}
                        </div>
                        <div className="text-muted-foreground">
                          <span className="font-medium">Últimas Férias:</span> {item.ultima_ferias ? formatDateDisplay(item.ultima_ferias) : 'Nunca tirou'}
                        </div>
                        <div className={item.tem_direito ? 'text-yellow-600 font-medium' : 'text-muted-foreground'}>
                          <span className="font-medium">Próximas Férias:</span> {item.tem_direito ? 'Já pode tirar!' : item.previsao_texto}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Adiantamentos */}
          <TabsContent value="adiantamentos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="h-5 w-5" /> Adiantamentos Pendentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PendingAdvances />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Bônus/Comissões */}
          <TabsContent value="bonus" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5" /> Bônus e Comissões
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {allBonuses.filter(b => !b.pago).length} pendentes | {allBonuses.filter(b => b.pago).length} pagos
                </p>
              </CardHeader>
              <CardContent>
                {allBonuses.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">Nenhum bônus registrado</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Funcionário</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allBonuses.map((bonus) => (
                        <TableRow key={bonus.id}>
                          <TableCell className="font-medium">{bonus.employee_nome}</TableCell>
                          <TableCell>{bonus.data.split('-').reverse().join('/')}</TableCell>
                          <TableCell>{bonus.descricao}</TableCell>
                          <TableCell className="text-right font-mono text-green-600">
                            +{formatCurrency(bonus.valor)}
                          </TableCell>
                          <TableCell>
                            {bonus.pago ? (
                              <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded">
                                Pago
                              </span>
                            ) : (
                              <span className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 px-2 py-1 rounded">
                                Pendente
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {!bonus.pago && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteBonus(bonus.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Pró-labore */}
          <TabsContent value="prolabore" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-5 w-5" /> Pró-labore dos Sócios
                </CardTitle>
                <Button size="sm" onClick={() => setProlaboreDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Novo
                </Button>
              </CardHeader>
              <CardContent>
                {prolabores.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Nenhum pró-labore registrado</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Beneficiário</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Obs.</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prolabores.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.beneficiario}</TableCell>
                          <TableCell>{formatDateDisplay(p.data)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(p.valor || 0)}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{p.observacao || '-'}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteProlabore(p.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog: Novo/Editar Funcionário */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}</DialogTitle>
              <DialogDescription>Preencha os dados do funcionário</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Nome *</Label>
                  <Input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} required />
                </div>
                <div>
                  <Label>CPF *</Label>
                  <Input value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: formatCPF(e.target.value) })} required />
                </div>
                <div>
                  <Label>RG</Label>
                  <Input value={formData.rg} onChange={(e) => setFormData({ ...formData, rg: e.target.value })} />
                </div>
                <div>
                  <Label>PIS</Label>
                  <Input value={formData.pis} onChange={(e) => setFormData({ ...formData, pis: e.target.value })} />
                </div>
                <div>
                  <Label>CTPS</Label>
                  <Input value={formData.ctps} onChange={(e) => setFormData({ ...formData, ctps: e.target.value })} />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input value={formData.telefone} onChange={(e) => setFormData({ ...formData, telefone: formatPhone(e.target.value) })} />
                </div>
                <div>
                  <Label>Cargo *</Label>
                  <Input value={formData.cargo} onChange={(e) => setFormData({ ...formData, cargo: e.target.value })} required />
                </div>
                <div className="sm:col-span-2">
                  <Label>Endereço</Label>
                  <Input value={formData.endereco} onChange={(e) => setFormData({ ...formData, endereco: e.target.value })} />
                </div>
                <div>
                  <Label>Data Admissão *</Label>
                  <Input type="date" value={formData.data_admissao} onChange={(e) => setFormData({ ...formData, data_admissao: e.target.value })} required />
                </div>
                <div>
                  <Label>Salário Base *</Label>
                  <Input type="number" step="0.01" value={formData.salario_base} onChange={(e) => setFormData({ ...formData, salario_base: e.target.value })} required />
                </div>
                <div>
                  <Label>Últimas Férias</Label>
                  <Input type="date" value={formData.ultima_ferias} onChange={(e) => setFormData({ ...formData, ultima_ferias: e.target.value })} />
                  <p className="text-xs text-muted-foreground mt-1">Data do fim das últimas férias (se houver)</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Dados Bancários</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label>Banco</Label>
                    <Input value={formData.banco} onChange={(e) => setFormData({ ...formData, banco: e.target.value })} />
                  </div>
                  <div>
                    <Label>Agência</Label>
                    <Input value={formData.agencia} onChange={(e) => setFormData({ ...formData, agencia: e.target.value })} />
                  </div>
                  <div>
                    <Label>Conta</Label>
                    <Input value={formData.conta} onChange={(e) => setFormData({ ...formData, conta: e.target.value })} />
                  </div>
                  <div>
                    <Label>PIX</Label>
                    <Input value={formData.pix} onChange={(e) => setFormData({ ...formData, pix: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit">{editingEmployee ? 'Salvar' : 'Cadastrar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Registrar Falta */}
        <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Falta</DialogTitle>
              <DialogDescription>{selectedEmployee?.nome}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={absenceData.data} onChange={(e) => setAbsenceData({ ...absenceData, data: e.target.value })} />
              </div>
              <div>
                <Label>Tipo de Falta *</Label>
                <Select value={absenceData.tipo} onValueChange={(v) => setAbsenceData({ ...absenceData, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dia_inteiro">Dia Inteiro</SelectItem>
                    <SelectItem value="meio_dia">Meio Dia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Motivo</Label>
                <Input value={absenceData.motivo} onChange={(e) => setAbsenceData({ ...absenceData, motivo: e.target.value })} placeholder="Ex: Consulta médica, problema pessoal..." />
              </div>
              <div>
                <Label>Valor Personalizado (opcional)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={absenceData.valor_personalizado} 
                  onChange={(e) => setAbsenceData({ ...absenceData, valor_personalizado: e.target.value })} 
                  placeholder="Deixe vazio para calcular automaticamente"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Se não informado, será calculado com base no salário ({absenceData.tipo === 'meio_dia' ? 'metade do dia' : 'dia inteiro'})
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="justificada"
                  checked={absenceData.justificada}
                  onChange={(e) => setAbsenceData({ ...absenceData, justificada: e.target.checked })}
                />
                <Label htmlFor="justificada">Falta justificada (não desconta)</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAbsenceDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAddAbsence}>Registrar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Adiantamento */}
        <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Adiantamento</DialogTitle>
              <DialogDescription>{selectedEmployee?.nome}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tipo *</Label>
                <Select value={advanceData.tipo} onValueChange={(v) => setAdvanceData({ ...advanceData, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salario">Adiantamento de Salário</SelectItem>
                    <SelectItem value="ferias">Adiantamento de Férias</SelectItem>
                    <SelectItem value="decimo_terceiro">Adiantamento de 13º</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor *</Label>
                <Input type="number" step="0.01" value={advanceData.valor} onChange={(e) => setAdvanceData({ ...advanceData, valor: e.target.value })} />
              </div>
              <div>
                <Label>Data *</Label>
                <Input type="date" value={advanceData.data} onChange={(e) => setAdvanceData({ ...advanceData, data: e.target.value })} />
              </div>
              <div>
                <Label>Conta Bancária (saída)</Label>
                <Select value={advanceData.conta_id} onValueChange={(v) => setAdvanceData({ ...advanceData, conta_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.nome} - Saldo: {formatCurrency(acc.saldo)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Se informado, será gerada transação de saída</p>
              </div>
              <div>
                <Label>Observação</Label>
                <Input value={advanceData.observacao} onChange={(e) => setAdvanceData({ ...advanceData, observacao: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAdvanceDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAddAdvance}>Registrar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Bonificação */}
        <Dialog open={bonusDialogOpen} onOpenChange={setBonusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Bonificação/Comissão</DialogTitle>
              <DialogDescription>{selectedEmployee?.nome}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Valor *</Label>
                <Input type="number" step="0.01" value={bonusData.valor} onChange={(e) => setBonusData({ ...bonusData, valor: e.target.value })} />
              </div>
              <div>
                <Label>Data *</Label>
                <Input type="date" value={bonusData.data} onChange={(e) => setBonusData({ ...bonusData, data: e.target.value })} />
              </div>
              <div>
                <Label>Descrição *</Label>
                <Input value={bonusData.descricao} onChange={(e) => setBonusData({ ...bonusData, descricao: e.target.value })} placeholder="Ex: Comissão venda X" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setBonusDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAddBonus}>Registrar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Hora Extra */}
        <Dialog open={overtimeDialogOpen} onOpenChange={setOvertimeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Hora Extra</DialogTitle>
              <DialogDescription>{selectedEmployee?.nome}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={overtimeData.data} onChange={(e) => setOvertimeData({ ...overtimeData, data: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Quantidade de Horas *</Label>
                  <Input type="number" step="0.5" value={overtimeData.horas} onChange={(e) => setOvertimeData({ ...overtimeData, horas: e.target.value })} placeholder="Ex: 2" />
                </div>
                <div>
                  <Label>Valor da Hora (R$) *</Label>
                  <Input type="number" step="0.01" value={overtimeData.valor_hora} onChange={(e) => setOvertimeData({ ...overtimeData, valor_hora: e.target.value })} placeholder="Ex: 15.00" />
                </div>
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={overtimeData.tipo} onValueChange={(v) => setOvertimeData({ ...overtimeData, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50% (Dias úteis)</SelectItem>
                    <SelectItem value="100">100% (Domingos/Feriados)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {overtimeData.horas && overtimeData.valor_hora && (
                <div className="p-3 bg-green-500/10 rounded text-sm">
                  <strong>Total:</strong> {overtimeData.horas}h x {formatCurrency(parseFloat(overtimeData.valor_hora || 0))} = <strong className="text-green-500">{formatCurrency(parseFloat(overtimeData.horas || 0) * parseFloat(overtimeData.valor_hora || 0))}</strong>
                </div>
              )}
              <div>
                <Label>Observação</Label>
                <Input value={overtimeData.observacao} onChange={(e) => setOvertimeData({ ...overtimeData, observacao: e.target.value })} placeholder="Ex: Finalização de pedido urgente" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOvertimeDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAddOvertime}>Registrar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Férias */}
        <Dialog open={vacationDialogOpen} onOpenChange={setVacationDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agendar Férias</DialogTitle>
              <DialogDescription>{selectedEmployee?.nome}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data Início *</Label>
                  <Input type="date" value={vacationData.data_inicio} onChange={(e) => setVacationData({ ...vacationData, data_inicio: e.target.value })} />
                </div>
                <div>
                  <Label>Data Fim *</Label>
                  <Input type="date" value={vacationData.data_fim} onChange={(e) => setVacationData({ ...vacationData, data_fim: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Dias</Label>
                <Input type="number" value={vacationData.dias} onChange={(e) => setVacationData({ ...vacationData, dias: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Valor Férias *</Label>
                  <Input type="number" step="0.01" value={vacationData.valor_ferias} onChange={(e) => setVacationData({ ...vacationData, valor_ferias: e.target.value })} />
                </div>
                <div>
                  <Label>Abono (1/3)</Label>
                  <Input type="number" step="0.01" value={vacationData.valor_abono} onChange={(e) => setVacationData({ ...vacationData, valor_abono: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Desconto Previdência (INSS)</Label>
                <Input type="number" step="0.01" placeholder="0,00" value={vacationData.desconto_previdencia} onChange={(e) => setVacationData({ ...vacationData, desconto_previdencia: e.target.value })} />
              </div>
              <div>
                <Label>Observação</Label>
                <Input value={vacationData.observacao} onChange={(e) => setVacationData({ ...vacationData, observacao: e.target.value })} />
              </div>
              {/* Resumo do valor líquido */}
              {vacationData.valor_ferias && (
                <div className="p-3 bg-muted/50 rounded-lg space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Férias:</span>
                    <span>{formatCurrency(parseFloat(vacationData.valor_ferias) || 0)}</span>
                  </div>
                  {vacationData.valor_abono && (
                    <div className="flex justify-between">
                      <span>Abono (1/3):</span>
                      <span>{formatCurrency(parseFloat(vacationData.valor_abono) || 0)}</span>
                    </div>
                  )}
                  {adiantamentosFeriasPendentes > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span>Adiantamento de Férias:</span>
                      <span>-{formatCurrency(adiantamentosFeriasPendentes)}</span>
                    </div>
                  )}
                  {vacationData.desconto_previdencia && parseFloat(vacationData.desconto_previdencia) > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Desconto Previdência:</span>
                      <span>-{formatCurrency(parseFloat(vacationData.desconto_previdencia) || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t pt-1 mt-1">
                    <span>Valor Líquido a Pagar:</span>
                    <span className="text-green-600">
                      {formatCurrency(
                        (parseFloat(vacationData.valor_ferias) || 0) + 
                        (parseFloat(vacationData.valor_abono) || 0) - 
                        (parseFloat(vacationData.desconto_previdencia) || 0) -
                        adiantamentosFeriasPendentes
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    * Uma conta a pagar será criada com vencimento na data de início das férias
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setVacationDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAddVacation}>Agendar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Pró-labore */}
        <Dialog open={prolaboreDialogOpen} onOpenChange={setProlaboreDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Pró-labore</DialogTitle>
              <DialogDescription>Retirada mensal dos sócios</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Beneficiário (Sócio) *</Label>
                <Input value={prolaboreData.beneficiario} onChange={(e) => setProlaboreData({ ...prolaboreData, beneficiario: e.target.value })} placeholder="Ex: Lucas, Alexandre" />
              </div>
              <div>
                <Label>Valor *</Label>
                <Input type="number" step="0.01" value={prolaboreData.valor} onChange={(e) => setProlaboreData({ ...prolaboreData, valor: e.target.value })} />
              </div>
              <div>
                <Label>Data *</Label>
                <Input type="date" value={prolaboreData.data} onChange={(e) => setProlaboreData({ ...prolaboreData, data: e.target.value })} />
              </div>
              <div>
                <Label>Conta de Pagamento</Label>
                <Select value={prolaboreData.conta_id || 'none'} onValueChange={(v) => setProlaboreData({ ...prolaboreData, conta_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Observação</Label>
                <Input value={prolaboreData.observacao} onChange={(e) => setProlaboreData({ ...prolaboreData, observacao: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setProlaboreDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAddProlabore}>Registrar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// Componente para listar adiantamentos pendentes
function PendingAdvances() {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdvances();
  }, []);

  const fetchAdvances = async () => {
    try {
      const res = await axios.get(`${API}/advances/pending`);
      setAdvances(res.data);
    } catch (error) {
      console.error('Erro ao carregar adiantamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover adiantamento?')) return;
    try {
      await axios.delete(`${API}/advances/${id}`);
      toast.success('Adiantamento removido!');
      fetchAdvances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover');
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'salario': return 'Salário';
      case 'ferias': return 'Férias';
      case 'decimo_terceiro': return '13º';
      default: return tipo;
    }
  };

  if (loading) return <p className="text-muted-foreground">Carregando...</p>;

  if (advances.length === 0) {
    return <p className="text-muted-foreground text-center py-4">Nenhum adiantamento pendente</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Funcionário</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Data</TableHead>
          <TableHead className="text-right">Valor</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {advances.map((adv) => (
          <TableRow key={adv.id}>
            <TableCell className="font-medium">{adv.employee_nome}</TableCell>
            <TableCell>
              <Badge variant="outline">{getTipoLabel(adv.tipo)}</Badge>
            </TableCell>
            <TableCell>{formatDateDisplay(adv.data)}</TableCell>
            <TableCell className="text-right font-mono">{formatCurrency(adv.valor)}</TableCell>
            <TableCell>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(adv.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
